import {
  BOT_MODELS,
  DEFAULT_BOT_MODEL,
  DEFAULT_TIME_CONTROL_ID,
  TIME_CONTROLS,
  findBotModel,
} from '@chess/core'
import { useCallback, useEffect, useState, type FormEvent, type JSX, type ReactNode } from 'react'
import { AccountMenu } from '../lib/AccountMenu.js'
import { enginePath, localPath, playPath, roomPath } from '../lib/router.js'
import type { Session } from '../lib/session.js'
import { loadName, loadRooms, saveName } from '../lib/storage.js'
import { unlockAudio } from '../sound/engine.js'

export interface HomeProps {
  readonly navigate: (to: string) => void
  readonly session: Session
  readonly onSettings: () => void
}

/** Which card is expanded. Exactly one at a time, or none. */
type Panel = 'online' | 'friend' | 'computer' | null

/* ── the three doors ──────────────────────────────────────────────────────── */

const KnightIcon = (): JSX.Element => (
  <svg viewBox="0 0 45 45" className="h-7 w-7" fill="currentColor" aria-hidden="true">
    <path d="M22 9.2c5.5 0 9.2 3.3 10.8 8.4 1.1 3.6 1.4 7.8 1.4 12.6v3.2H12.6v-3c0-3.6 2-6.2 5.8-8.2l3-1.6-1.6-2.2-4.4 3.4c-1.3 1-3 .8-3.8-.5-.8-1.3-.4-2.9.8-3.9l5-4.2V9.2c0-1.4 1.6-1.9 2.4-.8z" />
    <path d="M12.4 41.2c0-2.6 2.2-4.2 4.6-4.2h11c2.4 0 4.6 1.6 4.6 4.2z" />
  </svg>
)

const LinkIcon = (): JSX.Element => (
  <svg viewBox="0 0 24 24" className="h-7 w-7" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
    <path d="M10 13a5 5 0 0 0 7.5.5l3-3a5 5 0 0 0-7-7l-1.7 1.7" />
    <path d="M14 11a5 5 0 0 0-7.5-.5l-3 3a5 5 0 0 0 7 7l1.7-1.7" />
  </svg>
)

const ChipIcon = (): JSX.Element => (
  <svg viewBox="0 0 24 24" className="h-7 w-7" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" aria-hidden="true">
    <rect x="6" y="6" width="12" height="12" rx="2" />
    <path d="M9 3v3M15 3v3M9 18v3M15 18v3M3 9h3M3 15h3M18 9h3M18 15h3" />
  </svg>
)

/** An eye with a memory trace orbiting it — Jev watches, and remembers. */
const JevIcon = ({ className = 'h-7 w-7' }: { readonly className?: string }): JSX.Element => (
  <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M2 12c2.4-4.2 6-6.3 10-6.3s7.6 2.1 10 6.3c-2.4 4.2-6 6.3-10 6.3S4.4 16.2 2 12Z" />
    <circle cx="12" cy="12" r="3" fill="currentColor" stroke="none" />
    <circle cx="19.5" cy="6" r="1.4" fill="currentColor" stroke="none" />
    <path d="M15.5 8.2 18.3 7" strokeWidth="1.2" />
  </svg>
)

const Card = ({
  icon,
  title,
  subtitle,
  open,
  onToggle,
  children,
}: {
  readonly icon: ReactNode
  readonly title: string
  readonly subtitle: string
  readonly open: boolean
  readonly onToggle: () => void
  readonly children: ReactNode
}): JSX.Element => (
  <section
    className={`overflow-hidden rounded-panel transition-colors ${
      open ? 'bg-panel ring-1 ring-accent/40' : 'bg-panel'
    }`}
  >
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      className="flex w-full items-center gap-4 p-4 text-left hover:bg-panel-raised"
    >
      <span className={`shrink-0 ${open ? 'text-accent' : 'text-ink-dim'}`}>{icon}</span>
      <span className="min-w-0 flex-1">
        <span className="block text-base font-bold">{title}</span>
        <span className="mt-0.5 block text-xs text-ink-dim">{subtitle}</span>
      </span>
      <span className={`shrink-0 text-ink-dim transition-transform ${open ? 'rotate-90' : ''}`}>›</span>
    </button>
    {open && <div className="card-in border-t border-panel-line p-4">{children}</div>}
  </section>
)

const TimeControls = ({
  value,
  onChange,
}: {
  readonly value: string
  readonly onChange: (id: string) => void
}): JSX.Element => (
  <div className="grid grid-cols-3 gap-2">
    {TIME_CONTROLS.map((preset) => (
      <button
        key={preset.id}
        type="button"
        onClick={() => onChange(preset.id)}
        aria-pressed={value === preset.id}
        className={`rounded px-2 py-2.5 text-sm font-semibold transition-colors ${
          value === preset.id
            ? 'bg-accent text-black'
            : 'bg-panel-raised text-ink hover:bg-panel-line'
        }`}
      >
        {preset.label}
      </button>
    ))}
  </div>
)

/* ── the ask box, demoted ─────────────────────────────────────────────────── */

const AskFooter = (): JSX.Element => {
  const [open, setOpen] = useState(false)
  const [question, setQuestion] = useState('')
  const [answer, setAnswer] = useState<string | null>(null)
  const [asking, setAsking] = useState(false)

  const ask = async (event: FormEvent): Promise<void> => {
    event.preventDefault()
    if (question.trim() === '') return
    setAsking(true)
    setAnswer(null)
    try {
      const response = await fetch(`/api/ask?q=${encodeURIComponent(question)}`)
      const body = (await response.json()) as {
        message?: string
        response?: string
        error?: string
      }
      setAnswer(body.response ?? body.message ?? body.error ?? 'No answer came back.')
    } catch {
      setAnswer('Could not reach the server.')
    } finally {
      setAsking(false)
    }
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="text-xs font-semibold text-ink-dim hover:text-ink"
      >
        Explore the archive
      </button>
    )
  }

  return (
    <div className="card-in mt-2 w-full rounded-panel bg-panel p-4 text-left">
      <p className="text-xs text-ink-dim">
        Every finished game gets a short written summary; this searches those in plain English.
        Finished games are stored, including the moves and anything typed in chat.
      </p>
      <form onSubmit={(event) => void ask(event)} className="mt-3 flex gap-2">
        <input
          value={question}
          onChange={(event) => setQuestion(event.target.value)}
          placeholder="Which games ended in an early blunder?"
          className="min-w-0 flex-1 rounded bg-panel-raised px-3 py-2.5 text-sm text-ink outline-none placeholder:text-ink-dim focus:ring-1 focus:ring-accent"
        />
        <button
          type="submit"
          disabled={asking}
          className="shrink-0 rounded bg-panel-raised px-4 py-2.5 text-sm font-semibold hover:bg-panel-line disabled:opacity-50"
        >
          {asking ? '…' : 'Ask'}
        </button>
      </form>
      {answer !== null && (
        <p className="mt-3 rounded bg-panel-raised px-3 py-2 text-sm leading-relaxed text-ink">
          {answer}
        </p>
      )}
    </div>
  )
}

/* ── Jev, the featured opponent ───────────────────────────────────────────── */

/** The fields this page actually reads out of `GET /api/me/stats`. */
interface JevTeaserStats {
  readonly signedIn: boolean
  readonly games: number
  readonly openings?: readonly { readonly name: string; readonly n: number }[]
}

/**
 * One honest line about what Jev currently knows.
 *
 * `stats === null` covers both "still loading" and "the request failed" —
 * either way there is nothing yet to report, so the copy stays generic rather
 * than guessing.
 */
const jevTeaser = (stats: JevTeaserStats | null): string => {
  if (stats === null || !stats.signedIn) {
    return 'Sign in and it starts learning your game. Guests can still play — just not personally, yet.'
  }
  if (stats.games === 0) return 'No games on record yet — it starts learning the moment you play.'

  const opening = stats.openings?.[0]?.name
  return opening === undefined
    ? `${stats.games} game(s) on record. It already has a read on how you play.`
    : `${stats.games} game(s) on record — it's noticed you reach for ${opening} often.`
}

const JevHero = ({
  stats,
  creating,
  onPlay,
}: {
  readonly stats: JevTeaserStats | null
  readonly creating: boolean
  readonly onPlay: () => void
}): JSX.Element => (
  <section className="overflow-hidden rounded-panel bg-panel p-5 ring-1 ring-accent/40">
    <div className="flex items-center gap-2 text-accent">
      <JevIcon className="h-6 w-6" />
      <span className="text-[11px] font-bold tracking-wide uppercase">New opponent</span>
    </div>
    <h2 className="mt-2 text-2xl font-bold tracking-tight">Play Jev</h2>
    <p className="mt-1 text-sm text-ink-dim">
      Not a language model — a decision engine. It sees every legal move and everything your
      account has played before, and picks the move it thinks beats you specifically.
    </p>
    <p className="mt-3 text-xs font-semibold text-ink">{jevTeaser(stats)}</p>
    <button
      type="button"
      disabled={creating}
      onClick={onPlay}
      className="mt-4 w-full rounded bg-accent px-4 py-3 text-base font-bold text-black hover:bg-accent-strong disabled:opacity-50"
    >
      {creating ? 'Creating…' : 'Play Jev'}
    </button>
  </section>
)

/* ── the page ─────────────────────────────────────────────────────────────── */

/**
 * Jev first, then three doors, and one decision behind each.
 *
 * The name field and the time control used to sit in front of every visitor,
 * before they had decided they wanted to play at all. They now live inside the
 * flow that needs them. Jev sits above the three doors rather than inside the
 * "Play the computer" one — it is not just another model in that list, it is
 * the thing this page is here to show off.
 */
export const Home = ({ navigate, session, onSettings }: HomeProps): JSX.Element => {
  const [panel, setPanel] = useState<Panel>(null)
  const [name, setName] = useState(loadName)
  const [control, setControl] = useState(DEFAULT_TIME_CONTROL_ID)
  const [model, setModel] = useState(DEFAULT_BOT_MODEL)
  const [showModels, setShowModels] = useState(false)
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [jevStats, setJevStats] = useState<JevTeaserStats | null>(null)
  const [authFailed] = useState(
    () => new URLSearchParams(window.location.search).get('auth') === 'failed',
  )

  // A guest's typed name; a signed-in player already has one.
  useEffect(() => saveName(name), [name])

  // Guest or signed-in, `/api/me/stats` answers either way (`signedIn: false`
  // for a guest) — one request covers both cases in the teaser above.
  useEffect(() => {
    let cancelled = false
    fetch('/api/me/stats')
      .then((response) => response.json())
      .then((body: JevTeaserStats) => {
        if (!cancelled) setJevStats(body)
      })
      .catch(() => {
        /* leave jevStats null — the teaser has a generic fallback */
      })
    return () => {
      cancelled = true
    }
  }, [])

  const hasRooms = loadRooms().length > 0
  const displayName = session.user?.name ?? name

  const createRoom = useCallback(
    async (botModel?: string): Promise<void> => {
      unlockAudio()
      setCreating(true)
      setError(null)
      try {
        const response = await fetch('/api/rooms', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({
            // A model that thinks slowly can flag itself, so bot games get a
            // generous clock regardless of what is selected above.
            timeControl: botModel === undefined ? control : '10+0',
            ...(botModel === undefined ? {} : { botModel }),
            // The server runs in UTC and has no other way to know that it is
            // 2am where you are, which is when the bot gets strange.
            tzOffsetMinutes: new Date().getTimezoneOffset(),
          }),
        })
        if (!response.ok) throw new Error(`server said ${response.status}`)
        const created = (await response.json()) as { roomId: string; whiteKey: string }
        navigate(roomPath(created.roomId, created.whiteKey))
      } catch (cause) {
        setError(cause instanceof Error ? cause.message : 'could not reach the server')
        setCreating(false)
      }
    },
    [control, navigate],
  )

  const toggle = (which: Panel): void => setPanel((current) => (current === which ? null : which))

  return (
    <main className="mx-auto w-full max-w-lg px-4 py-6 safe-top safe-bottom">
      <header className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Chess</h1>
          <p className="mt-0.5 text-sm text-ink-dim">
            {session.user === null
              ? 'Play a stranger, a friend, or a language model.'
              : `Welcome back, ${session.user.name}.`}
          </p>
        </div>
        <AccountMenu session={session} onSettings={onSettings} onGames={() => navigate('/me')} />
      </header>

      {authFailed && (
        <p className="mt-4 rounded bg-danger/20 px-3 py-2 text-sm">
          That sign-in did not go through. You can play as a guest, or try again.
        </p>
      )}

      <div className="mt-6">
        <JevHero
          stats={jevStats}
          creating={creating}
          onPlay={() => {
            unlockAudio()
            void createRoom('jev')
          }}
        />
        {error !== null && (
          <p className="mt-2 text-sm text-danger">Could not create a room — {error}.</p>
        )}
      </div>

      <div className="mt-4 space-y-2">
        <Card
          icon={<KnightIcon />}
          title="Play online"
          subtitle="Get matched with whoever is around"
          open={panel === 'online'}
          onToggle={() => toggle('online')}
        >
          <TimeControls value={control} onChange={setControl} />
          <button
            type="button"
            onClick={() => {
              unlockAudio()
              navigate(playPath(control))
            }}
            className="mt-3 w-full rounded bg-accent px-4 py-3 text-base font-bold text-black hover:bg-accent-strong"
          >
            Find a match
          </button>
          <p className="mt-2 text-center text-[11px] text-ink-dim">
            Quiet right now? After 30 seconds you can play the computer instead.
          </p>
        </Card>

        <Card
          icon={<LinkIcon />}
          title="Play a friend"
          subtitle="Make a room and send them the link"
          open={panel === 'friend'}
          onToggle={() => toggle('friend')}
        >
          {session.user === null && (
            <label className="block text-xs font-semibold tracking-wide text-ink-dim uppercase">
              Your name
              <input
                value={name}
                onChange={(event) => setName(event.target.value)}
                maxLength={20}
                placeholder="Anonymous"
                className="mt-1.5 w-full rounded bg-panel-raised px-3 py-2.5 text-base font-normal normal-case tracking-normal text-ink outline-none placeholder:text-ink-dim focus:ring-1 focus:ring-accent"
              />
            </label>
          )}

          <p className="mt-3 mb-1.5 text-xs font-semibold tracking-wide text-ink-dim uppercase">
            Time control
          </p>
          <TimeControls value={control} onChange={setControl} />

          <button
            type="button"
            disabled={creating}
            onClick={() => void createRoom()}
            className="mt-3 w-full rounded bg-accent px-4 py-3 text-base font-bold text-black hover:bg-accent-strong disabled:opacity-50"
          >
            {creating ? 'Creating…' : 'Create a room'}
          </button>

          <button
            type="button"
            onClick={() => {
              unlockAudio()
              navigate(localPath(control))
            }}
            className="mt-2 w-full rounded px-4 py-2.5 text-sm font-semibold text-ink-dim hover:text-ink"
          >
            Or play both sides on this device
          </button>
        </Card>

        <Card
          icon={<ChipIcon />}
          title="Play the computer"
          subtitle="Jev, a language model, or a raw heuristic baseline"
          open={panel === 'computer'}
          onToggle={() => toggle('computer')}
        >
          <p className="text-xs text-ink-dim">
            The language models here play badly and sometimes illegally — a greedy fallback takes
            over when one can't find a legal move — and talk back if you chat with them. Jev is up
            above: it never plays an illegal move, and it is what this page is really about.
          </p>

          <button
            type="button"
            onClick={() => setShowModels((value) => !value)}
            className="mt-3 text-xs font-semibold text-ink-dim hover:text-ink"
          >
            {showModels ? 'Hide models' : `Model: ${findBotModel(model)?.label ?? model}`}
          </button>

          {showModels && (
            <ul className="mt-2 space-y-1">
              {BOT_MODELS.map((option) => (
                <li key={option.id}>
                  <button
                    type="button"
                    onClick={() => setModel(option.id)}
                    aria-pressed={model === option.id}
                    className={`w-full rounded px-3 py-2 text-left transition-colors ${
                      model === option.id
                        ? 'bg-panel-raised ring-1 ring-accent'
                        : 'hover:bg-panel-raised'
                    }`}
                  >
                    <span className="flex items-baseline justify-between gap-2">
                      <span className="text-sm font-semibold">{option.label}</span>
                      <span className="shrink-0 font-mono text-[10px] text-ink-dim">
                        {option.inputPricePerM === undefined
                          ? 'free'
                          : `$${option.inputPricePerM}/M in`}
                      </span>
                    </span>
                    <span className="mt-0.5 block text-xs text-ink-dim">{option.note}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}

          <button
            type="button"
            disabled={creating}
            onClick={() => {
              // Stockfish never touches the server: the game runs in this
              // browser, so it navigates instead of creating a room.
              if (findBotModel(model)?.kind === 'engine') {
                unlockAudio()
                navigate(enginePath('10+0', model))
              } else {
                void createRoom(model)
              }
            }}
            className="mt-3 w-full rounded bg-accent px-4 py-3 text-base font-bold text-black hover:bg-accent-strong disabled:opacity-50"
          >
            {creating ? 'Creating…' : `Play ${findBotModel(model)?.label ?? model}`}
          </button>
          <p className="mt-1.5 text-center text-[11px] text-ink-dim">
            {findBotModel(model)?.kind === 'engine'
              ? 'Runs in your browser. The game is local to this device and is not saved.'
              : 'Computer games are 10 minutes — a slow model can run itself out of time.'}
          </p>
        </Card>
      </div>

      <footer className="mt-8 flex flex-col items-center gap-3 text-center">
        <div className="flex items-center gap-4">
          {(session.user !== null || hasRooms) && (
            <button
              type="button"
              onClick={() => navigate('/me')}
              className="text-xs font-semibold text-ink-dim hover:text-ink"
            >
              Your games
            </button>
          )}
          <button
            type="button"
            onClick={() => navigate('/arena')}
            className="text-xs font-semibold text-ink-dim hover:text-ink"
          >
            Watch AI vs AI
          </button>
          <button
            type="button"
            onClick={onSettings}
            className="text-xs font-semibold text-ink-dim hover:text-ink"
          >
            Settings
          </button>
          <AskFooter />
        </div>
        <p className="text-[11px] text-ink-dim">
          {session.user === null
            ? 'No account needed. Signing in only means being recognised on another device.'
            : `Signed in as ${displayName}. Guests can do everything you can.`}
        </p>
      </footer>
    </main>
  )
}
